/* eslint-disable import/no-anonymous-default-export */
import { Alert, Button, Card, DatePicker, Form, InputNumber, message, notification, Radio, Select, Space, Switch, Table, Tag, Tooltip, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import locale from "antd/lib/date-picker/locale/es_ES";
import { CalculatorOutlined, ExportOutlined, FilePdfOutlined, InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import UsuariosContext from "../../common/contexts/UsuariosContext";
import { ActionResult } from "../../common/hooks/useApi";
import useEquiposAPI, { Equipo } from "../../equipos/useEquiposAPI";
import useInfraccionesAPI, { EstadoInfraccion } from "../../infracciones/hooks/useInfraccionesAPI";
import useUnidadesProduccionAPI, { UnidadProduccion } from "../../unidades-produccion/useUnidadesProduccionAPI";
import useUsuariosAPI, { Usuario } from "../../usuarios/useUsuariosAPI";

const { RangePicker } = DatePicker;

const PROP_FECHA = "FechaRenombrado";
const STORAGE_KEY = "resumen-renombrado-formula";
const CONCURRENCIA = 4;

const FORMULA_DEFAULT = { ImporteInicial: 45, FactorCorreccion: 6 };

/** Usuarios habilitados a ver y editar la fórmula del importe. La restricción es por usuario, no por rol. */
const USUARIOS_FORMULA_FALLBACK = ["miltonbelloni", "jwuignier", "edub"];

const getUsuariosPermitidosFormula = (): string[] => {
    let permitidos: string[] = [];

    try {
        permitidos = JSON.parse(process.env.REACT_APP_USUARIOS_FORMULA_RENOMBRADO ?? "[]");
    } catch { }

    return (permitidos.length ? permitidos : USUARIOS_FORMULA_FALLBACK)
        .map(u => String(u).trim().toLowerCase());
};

const Filters = styled.div`
display: grid;
grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
gap: 1rem;
align-items: end;
margin-bottom: 1rem;
`;

const FiltersButtons = styled.div`
display: flex;
justify-content: space-between;
gap: 1rem;
`;

type TipoPeriodo = ("MES" | "RANGO");

interface ResumenFilter {
    TipoPeriodo: TipoPeriodo
    Mes: Moment
    Rango: [Moment, Moment]
    RenombradorIds?: number[]
    UnidadProduccionId?: number
    EquipoId?: number
}

interface FormulaParams {
    ImporteInicial: number
    FactorCorreccion: number
}

interface ConsultaInfo {
    Periodo: string
    UnidadProduccion: string
    Equipo: string
    Renombradores: string
}

export interface FilaResumen {
    key: number
    Renombrador: string
    Renombradas: number
    Efectivas: number
    Ilegibles: number
}

/** Equivalente a REDONDEAR(valor;decimales) de la planilla (redondeo hacia afuera del cero). */
const redondear = (valor: number, decimales: number) => {
    const factor = Math.pow(10, decimales);
    return Math.sign(valor) * Math.round(Math.abs(valor) * factor) / factor;
};

/** =B2*(45+(REDONDEAR(LOG10(B2);2)*6)) donde B2 = cantidad de efectivas */
export const calcularImporte = (efectivas: number, formula: FormulaParams) => {
    if (!efectivas || efectivas <= 0) {
        return 0;
    }
    return efectivas * (formula.ImporteInicial + (redondear(Math.log10(efectivas), 2) * formula.FactorCorreccion));
};

const formatoMoneda = (valor: number) => `$ ${new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)}`;

const escapeHtml = (valor: any) => String(valor ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const leerFormulaGuardada = (): FormulaParams => {
    try {
        const guardado = localStorage.getItem(STORAGE_KEY);
        return guardado ? { ...FORMULA_DEFAULT, ...JSON.parse(guardado) } : FORMULA_DEFAULT;
    } catch {
        return FORMULA_DEFAULT;
    }
};

export default () => {
    const apiInfracciones = useInfraccionesAPI();
    const apiRenombradores = useUsuariosAPI();
    const apiUnidadesProduccion = useUnidadesProduccionAPI();
    const apiEquipos = useEquiposAPI();
    const usuariosContext = useContext(UsuariosContext);

    const [form] = useForm<ResumenFilter>();
    const [formFormula] = useForm<FormulaParams>();

    const [renombradoresResponse, setRenombradoresResponse] = useState<ActionResult<Usuario[]>>();
    const [unidadesProduccionResponse, setUnidadesProduccionResponse] = useState<ActionResult<UnidadProduccion[]>>();
    const [equiposResponse, setEquiposResponse] = useState<ActionResult<Equipo[]>>();

    const [tipoPeriodo, setTipoPeriodo] = useState<TipoPeriodo>("MES");
    const [unidadProduccionId, setUnidadProduccionId] = useState<number>();
    const [formula, setFormula] = useState<FormulaParams>(leerFormulaGuardada());
    const [formulaBorrador, setFormulaBorrador] = useState<FormulaParams>(formula);

    const [loading, setLoading] = useState(false);
    const [progreso, setProgreso] = useState<{ actual: number, total: number }>();
    const [filas, setFilas] = useState<FilaResumen[]>();
    const [consulta, setConsulta] = useState<ConsultaInfo>();
    const [ocultarSinActividad, setOcultarSinActividad] = useState(true);

    const usuarioActual = useMemo(() => {
        if (usuariosContext?.user) {
            return usuariosContext.user;
        }
        try {
            return JSON.parse(localStorage.getItem("_user") ?? "null") ?? undefined;
        } catch {
            return undefined;
        }
    }, [usuariosContext?.user]);

    /** La fórmula solo la ven los usuarios habilitados explícitamente (por nombre de usuario, no por rol). */
    const puedeVerFormula = useMemo(
        () => getUsuariosPermitidosFormula().includes(String(usuarioActual?.nombreUsuario ?? "").trim().toLowerCase()),
        [usuarioActual]);

    useEffect(() => {
        apiRenombradores
            .list({ $filter: ["Role/Nombre eq 'Renombrador'"], $orderby: ["Apellido asc"] })
            .then(setRenombradoresResponse);
        apiUnidadesProduccion.list({}).then(setUnidadesProduccionResponse);
    }, []);

    useEffect(() => {
        const $filter = unidadProduccionId ? [`ConfiguracionProduccion/UnidadProduccion/Id eq ${unidadProduccionId}`] : [];
        apiEquipos.list({ $expand: ["ConfiguracionProduccion($expand=UnidadProduccion)"], $filter }).then(setEquiposResponse);
    }, [unidadProduccionId]);

    const getFiltrosPeriodo = (data: ResumenFilter) => {
        if (data.TipoPeriodo === "RANGO") {
            const [desde, hasta] = data.Rango;
            return [
                `${PROP_FECHA} ge ${desde.clone().startOf("day").toISOString()}`,
                `${PROP_FECHA} le ${hasta.clone().endOf("day").toISOString()}`
            ];
        }
        return [
            `year(${PROP_FECHA}) eq ${data.Mes.year()}`,
            `month(${PROP_FECHA}) eq ${data.Mes.month() + 1}`
        ];
    };

    const getDescripcionPeriodo = (data: ResumenFilter) => data.TipoPeriodo === "RANGO"
        ? `${data.Rango[0].format("DD/MM/YYYY")} al ${data.Rango[1].format("DD/MM/YYYY")}`
        : `${data.Mes.format("MMMM [de] YYYY")}`;

    const contar = async ($filter: string[]) => {
        const response = await apiInfracciones.count({ $filter });
        if (response.error) {
            throw response.error;
        }
        return response.data!.value as number;
    };

    const getFilaRenombrador = async (renombrador: Usuario, filtrosBase: string[]): Promise<FilaResumen> => {
        const $filter = [...filtrosBase, `LoteRenombrado/RenombradorAsignadoId eq ${renombrador.Id}`];

        const [Renombradas, Efectivas, Ilegibles] = await Promise.all([
            contar([...$filter, `Estado ne '${"PENDIENTE" as EstadoInfraccion}'`]),
            contar([...$filter, `Estado ne '${"PENDIENTE" as EstadoInfraccion}'`, "NoDistinguible eq false"]),
            contar([...$filter, `Estado eq '${"RENOMBRADA" as EstadoInfraccion}'`, "NoDistinguible eq true"])
        ]);

        return {
            key: renombrador.Id,
            Renombrador: `${renombrador.Apellido} ${renombrador.Nombre}`,
            Renombradas,
            Efectivas,
            Ilegibles
        };
    };

    const onSubmit = async () => {
        const data = form.getFieldsValue();

        const filtrosBase = [
            ...getFiltrosPeriodo(data),
            data.EquipoId ? `EquipoId eq ${data.EquipoId}` : "",
            data.UnidadProduccionId ? `Equipo/ConfiguracionProduccion/UnidadProduccionId eq ${data.UnidadProduccionId}` : ""
        ].filter(f => f != "");

        const renombradores = (renombradoresResponse?.data?.value ?? [])
            .filter(r => !data.RenombradorIds?.length || data.RenombradorIds.includes(r.Id));

        setLoading(true);
        setProgreso({ actual: 0, total: renombradores.length });

        try {
            const resultado: FilaResumen[] = [];

            for (let i = 0; i < renombradores.length; i += CONCURRENCIA) {
                const lote = renombradores.slice(i, i + CONCURRENCIA);
                const filasLote = await Promise.all(lote.map(r => getFilaRenombrador(r, filtrosBase)));
                resultado.push(...filasLote);
                setProgreso({ actual: Math.min(i + CONCURRENCIA, renombradores.length), total: renombradores.length });
            }

            setFilas(resultado.sort((a, b) => b.Efectivas - a.Efectivas));
            setConsulta({
                Periodo: getDescripcionPeriodo(data),
                UnidadProduccion: unidadesProduccionResponse?.data?.value.find(u => u.Id === data.UnidadProduccionId)?.Nombre ?? "Todas",
                Equipo: equiposResponse?.data?.value.find(e => e.Id === data.EquipoId)?.Nombre ?? "Todos",
                Renombradores: data.RenombradorIds?.length
                    ? renombradores.map(r => `${r.Apellido} ${r.Nombre}`).join(", ")
                    : "Todos"
            });
        } catch (error) {
            notification.error({ message: "Error al cargar el resumen de renombrado" });
        }

        setProgreso(undefined);
        setLoading(false);
    };

    const onChangeFormula = (_: any, values: FormulaParams) => {
        setFormulaBorrador({
            ImporteInicial: values.ImporteInicial ?? FORMULA_DEFAULT.ImporteInicial,
            FactorCorreccion: values.FactorCorreccion ?? FORMULA_DEFAULT.FactorCorreccion
        });
    };

    /** Recalcula los importes con los datos ya consultados, sin volver a pegarle a la API. */
    const recalcular = () => {
        setFormula(formulaBorrador);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(formulaBorrador));
        } catch { }
        message.success("Importes recalculados con la nueva fórmula");
    };

    const formulaPendiente = formulaBorrador.ImporteInicial !== formula.ImporteInicial
        || formulaBorrador.FactorCorreccion !== formula.FactorCorreccion;

    const filasVisibles = useMemo(() => (filas ?? [])
        .filter(f => !ocultarSinActividad || f.Renombradas > 0)
        .map(f => ({ ...f, Importe: calcularImporte(f.Efectivas, formula) })),
        [filas, ocultarSinActividad, formula]);

    const totales = useMemo(() => filasVisibles.reduce((acc, f) => ({
        Renombradas: acc.Renombradas + f.Renombradas,
        Efectivas: acc.Efectivas + f.Efectivas,
        Ilegibles: acc.Ilegibles + f.Ilegibles,
        Importe: acc.Importe + f.Importe
    }), { Renombradas: 0, Efectivas: 0, Ilegibles: 0, Importe: 0 }), [filasVisibles]);

    const getUsuarioReporte = () => {
        const usuario = usuarioActual;

        return usuario
            ? `${usuario.apellido} ${usuario.nombre} (${usuario.nombreUsuario})`
            : "-";
    };

    const exportarCsv = () => {
        const encabezados = ["Renombrador", "Renombradas", "Efectivas", "Ilegibles", "Efectividad %", "Importe"];
        const lineas = filasVisibles.map(f => [
            `"${f.Renombrador}"`,
            f.Renombradas,
            f.Efectivas,
            f.Ilegibles,
            f.Renombradas ? (f.Efectivas * 100 / f.Renombradas).toFixed(2) : "0.00",
            f.Importe.toFixed(2)
        ].join(","));

        const csv = [
            `"Resumen de renombrado - ${consulta?.Periodo ?? ""}"`,
            `"U. producción: ${consulta?.UnidadProduccion ?? "Todas"}"`,
            `"Equipo: ${consulta?.Equipo ?? "Todos"}"`,
            `"Renombradores: ${consulta?.Renombradores ?? "Todos"}"`,
            ...(puedeVerFormula ? [`"Fórmula: Efectivas * (${formula.ImporteInicial} + REDONDEAR(LOG10(Efectivas);2) * ${formula.FactorCorreccion})"`] : []),
            `"Generado el ${moment().format("DD/MM/YYYY [a las] HH:mm [hs]")} por ${getUsuarioReporte()}"`,
            "",
            encabezados.join(","),
            ...lineas,
            "",
            ["TOTAL", totales.Renombradas, totales.Efectivas, totales.Ilegibles, "", totales.Importe.toFixed(2)].join(",")
        ].join("\n");

        const url = window.URL.createObjectURL(new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `resumen-renombrado-${moment().format("YYYYMMDD-HHmm")}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const generarPdf = () => {
        const generadoEl = moment().format("DD/MM/YYYY [a las] HH:mm [hs]");
        const nombreArchivo = `resumen-renombrado-${moment().format("YYYYMMDD-HHmm")}`;

        const ventana = window.open("", "_blank");

        if (!ventana) {
            notification.warning({
                message: "No se pudo abrir la ventana del PDF",
                description: "Habilitá las ventanas emergentes para este sitio e intentá nuevamente."
            });
            return;
        }

        const filasHtml = filasVisibles.map((f, indice) => `
            <tr${indice % 2 ? ' class="par"' : ""}>
                <td class="nombre">${escapeHtml(f.Renombrador)}</td>
                <td class="num">${f.Renombradas.toLocaleString("es-AR")}</td>
                <td class="num efectivas">${f.Efectivas.toLocaleString("es-AR")}</td>
                <td class="num ilegibles">${f.Ilegibles.toLocaleString("es-AR")}</td>
                <td class="num">${f.Renombradas ? `${(f.Efectivas * 100 / f.Renombradas).toFixed(2)}%` : "-"}</td>
                <td class="num importe">${escapeHtml(formatoMoneda(f.Importe))}</td>
            </tr>`).join("");

        const html = `<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="utf-8" />
    <title>${escapeHtml(nombreArchivo)}</title>
    <style>
        @page { size: A4 portrait; margin: 12mm; }

        * { box-sizing: border-box; }

        body {
            font-family: "Segoe UI", Arial, Helvetica, sans-serif;
            color: #262626;
            font-size: 11.5px;
            margin: 0;
        }

        .encabezado {
            border-left: 6px solid #2145D9;
            background: #f4f6fe;
            padding: 14px 18px;
            margin-bottom: 20px;
        }
        .marca {
            font-size: 10px;
            letter-spacing: 2px;
            text-transform: uppercase;
            color: #2145D9;
            font-weight: bold;
            margin-bottom: 6px;
        }
        .encabezado h1 {
            font-size: 21px;
            margin: 0;
            color: #14227a;
            letter-spacing: -0.2px;
        }
        .encabezado .periodo {
            font-size: 13px;
            color: #545454;
            margin-top: 4px;
        }

        .tarjetas {
            display: flex;
            gap: 10px;
            margin-bottom: 20px;
        }
        .tarjeta {
            flex: 1;
            border: 1px solid #e4e7ef;
            border-top: 3px solid #9aa7d6;
            border-radius: 5px;
            padding: 10px 12px;
            background: #ffffff;
        }
        .tarjeta.destacada { border-top-color: #2145D9; background: #f7f9ff; }
        .tarjeta .etiqueta {
            font-size: 9.5px;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            color: #7a7a7a;
            margin-bottom: 5px;
        }
        .tarjeta .valor { font-size: 17px; font-weight: bold; color: #14227a; }

        .seccion {
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.8px;
            color: #2145D9;
            padding-bottom: 5px;
            border-bottom: 2px solid #e4e7ef;
            margin-bottom: 10px;
        }

        .datos {
            display: flex;
            flex-wrap: wrap;
            border: 1px solid #e4e7ef;
            border-radius: 5px;
            padding: 12px 14px;
            margin-bottom: 22px;
            background: #fbfbfd;
        }
        .datos .dato { width: 50%; padding: 4px 0; }
        .datos .dato.ancho { width: 100%; border-top: 1px dashed #e0e0e0; margin-top: 6px; padding-top: 8px; }
        .datos b { color: #545454; font-weight: 600; }
        .datos span { color: #262626; }

        table { width: 100%; border-collapse: collapse; }
        thead th {
            background: #2145D9;
            color: #ffffff;
            font-size: 10.5px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 9px 10px;
            text-align: left;
            border: 1px solid #2145D9;
        }
        tbody td { padding: 8px 10px; border: 1px solid #e4e7ef; }
        tbody tr.par td { background: #eff3fd; }
        td.num, th.num { text-align: right; }
        td.nombre { font-weight: 600; }
        td.efectivas { font-weight: bold; color: #1b5e20; }
        td.ilegibles { color: #b3261e; }
        td.importe { font-weight: 600; }

        tfoot td {
            background: #eaeffc;
            font-weight: bold;
            color: #14227a;
            padding: 9px 10px;
            border: 1px solid #c9d4f5;
            border-top: 2px solid #2145D9;
        }

        .pie {
            margin-top: 18px;
            padding-top: 10px;
            border-top: 1px solid #e4e7ef;
            font-size: 9.5px;
            color: #8c8c8c;
            line-height: 1.5;
        }

        tr { page-break-inside: avoid; }
        thead { display: table-header-group; }
        tfoot { display: table-row-group; }

        @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
    </style>
</head>
<body>
    <div class="encabezado">
        <div class="marca">${escapeHtml(process.env.REACT_APP_NAME ?? "Neuralsys")}</div>
        <h1>Resumen de renombrado</h1>
        <div class="periodo">${escapeHtml(consulta?.Periodo ?? "-")}</div>
    </div>

    <div class="tarjetas">
        <div class="tarjeta">
            <div class="etiqueta">Renombradas</div>
            <div class="valor">${totales.Renombradas.toLocaleString("es-AR")}</div>
        </div>
        <div class="tarjeta destacada">
            <div class="etiqueta">Efectivas</div>
            <div class="valor">${totales.Efectivas.toLocaleString("es-AR")}</div>
        </div>
        <div class="tarjeta">
            <div class="etiqueta">Ilegibles</div>
            <div class="valor">${totales.Ilegibles.toLocaleString("es-AR")}</div>
        </div>
        <div class="tarjeta destacada">
            <div class="etiqueta">Importe total</div>
            <div class="valor">${escapeHtml(formatoMoneda(totales.Importe))}</div>
        </div>
    </div>

    <div class="seccion">Datos del reporte</div>
    <div class="datos">
        <div class="dato"><b>Generado el:</b> <span>${escapeHtml(generadoEl)}</span></div>
        <div class="dato"><b>Generado por:</b> <span>${escapeHtml(getUsuarioReporte())}</span></div>
        <div class="dato"><b>U. producción:</b> <span>${escapeHtml(consulta?.UnidadProduccion ?? "Todas")}</span></div>
        <div class="dato"><b>Equipo:</b> <span>${escapeHtml(consulta?.Equipo ?? "Todos")}</span></div>
        <div class="dato ancho"><b>Renombradores:</b> <span>${escapeHtml(consulta?.Renombradores ?? "Todos")}</span></div>
        ${puedeVerFormula ? `<div class="dato ancho"><b>Fórmula del importe:</b> <span>Efectivas × (${formula.ImporteInicial} + REDONDEAR(LOG10(Efectivas); 2) × ${formula.FactorCorreccion})</span></div>` : ""}
    </div>

    <div class="seccion">Detalle por renombrador</div>
    <table>
        <thead>
            <tr>
                <th>Renombrador</th>
                <th class="num">Renombradas</th>
                <th class="num">Efectivas</th>
                <th class="num">Ilegibles</th>
                <th class="num">Efectividad</th>
                <th class="num">Importe</th>
            </tr>
        </thead>
        <tbody>${filasHtml}</tbody>
        <tfoot>
            <tr>
                <td>Total (${filasVisibles.length} ${filasVisibles.length === 1 ? "renombrador" : "renombradores"})</td>
                <td class="num">${totales.Renombradas.toLocaleString("es-AR")}</td>
                <td class="num">${totales.Efectivas.toLocaleString("es-AR")}</td>
                <td class="num">${totales.Ilegibles.toLocaleString("es-AR")}</td>
                <td class="num">${totales.Renombradas ? `${(totales.Efectivas * 100 / totales.Renombradas).toFixed(2)}%` : "-"}</td>
                <td class="num">${escapeHtml(formatoMoneda(totales.Importe))}</td>
            </tr>
        </tfoot>
    </table>

    <div class="pie">
        Las cantidades corresponden a infracciones renombradas dentro del período indicado (fecha de renombrado), sin importar cuándo se asignó el lote.<br />
        ${escapeHtml(process.env.REACT_APP_NAME ?? "Neuralsys")} · Reporte generado el ${escapeHtml(generadoEl)} por ${escapeHtml(getUsuarioReporte())}.
    </div>
</body>
</html>`;

        ventana.document.open();
        ventana.document.write(html);
        ventana.document.close();

        ventana.onafterprint = () => ventana.close();
        setTimeout(() => {
            ventana.focus();
            ventana.print();
        }, 300);
    };

    return (
        <>
            <Form
                form={form}
                onFinish={onSubmit}
                layout="vertical"
                initialValues={{ TipoPeriodo: "MES" as TipoPeriodo, Mes: moment(), Rango: [moment().startOf("month"), moment()] }}
                style={{ marginBottom: "1rem" }}>

                <Form.Item name="TipoPeriodo" label="Período">
                    <Radio.Group optionType="button" buttonStyle="solid" onChange={(e) => setTipoPeriodo(e.target.value)}>
                        <Radio.Button value={"MES" as TipoPeriodo}>Mes</Radio.Button>
                        <Radio.Button value={"RANGO" as TipoPeriodo}>Rango de fechas</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                <Filters>
                    {tipoPeriodo === "MES"
                        ? <Form.Item
                            style={{ marginBottom: 0 }}
                            name="Mes"
                            label="Mes"
                            rules={[{ required: true, message: "Requerido" }]}>
                            <DatePicker picker="month" locale={locale} format={"MMMM YYYY"} allowClear={false} style={{ width: "100%" }} />
                        </Form.Item>
                        : <Form.Item
                            style={{ marginBottom: 0 }}
                            name="Rango"
                            label="Desde / hasta"
                            rules={[{ required: true, message: "Requerido" }]}>
                            <RangePicker locale={locale} format={"DD/MM/YYYY"} allowClear={false} style={{ width: "100%" }} />
                        </Form.Item>}

                    <Form.Item style={{ marginBottom: 0 }} name="UnidadProduccionId" label="U. producción">
                        <Select
                            loading={!unidadesProduccionResponse}
                            allowClear
                            onChange={(value: number) => {
                                setUnidadProduccionId(value);
                                form.setFieldsValue({ EquipoId: undefined });
                            }}>
                            {unidadesProduccionResponse?.data?.value.map(u =>
                                <Select.Option value={u.Id} key={u.Id}>{u.Nombre}</Select.Option>)}
                        </Select>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }} name="EquipoId" label="Equipo">
                        <Select loading={!equiposResponse} allowClear showSearch optionFilterProp="children">
                            {equiposResponse?.data?.value.map(e =>
                                <Select.Option value={e.Id} key={e.Id}>{e.Nombre} ({e.ConfiguracionProduccion?.UnidadProduccion?.Nombre})</Select.Option>)}
                        </Select>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }} name="RenombradorIds" label="Renombrador">
                        <Select
                            mode="multiple"
                            placeholder="Todos"
                            loading={!renombradoresResponse}
                            allowClear
                            showSearch
                            optionFilterProp="children">
                            {renombradoresResponse?.data?.value.map(r =>
                                <Select.Option value={r.Id} key={r.Id}>{r.Apellido} {r.Nombre}</Select.Option>)}
                        </Select>
                    </Form.Item>
                </Filters>

                <FiltersButtons>
                    <Button loading={loading} type="primary" htmlType="submit" icon={<SearchOutlined />}>Filtrar</Button>
                    <Space>
                        <Button disabled={!filasVisibles.length} onClick={generarPdf} icon={<FilePdfOutlined />}>Generar PDF</Button>
                        <Button disabled={!filasVisibles.length} onClick={exportarCsv} icon={<ExportOutlined />}>Exportar CSV</Button>
                    </Space>
                </FiltersButtons>
            </Form>

            {puedeVerFormula && <Card
                size="small"
                style={{ marginBottom: "1rem" }}
                title={<Space><InfoCircleOutlined />Fórmula del importe</Space>}
                extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>Recalcula sobre los datos ya consultados, sin volver a filtrar</Typography.Text>}>
                <Form
                    form={formFormula}
                    layout="inline"
                    initialValues={formula}
                    onValuesChange={onChangeFormula}>
                    <Form.Item name="ImporteInicial" label="Importe inicial">
                        <InputNumber min={0} step={1} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name="FactorCorreccion" label="Factor de corrección">
                        <InputNumber min={0} step={1} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            icon={<CalculatorOutlined />}
                            disabled={!formulaPendiente}
                            onClick={recalcular}>
                            Recalcular
                        </Button>
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="link"
                            onClick={() => {
                                formFormula.setFieldsValue(FORMULA_DEFAULT);
                                onChangeFormula(null, FORMULA_DEFAULT);
                            }}>
                            Restaurar valores por defecto
                        </Button>
                    </Form.Item>
                </Form>
                <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                    <code>Importe = Efectivas × ({formula.ImporteInicial} + REDONDEAR(LOG10(Efectivas); 2) × {formula.FactorCorreccion})</code>
                    {formulaPendiente &&
                        <Tag color="warning" style={{ marginLeft: 8 }}>
                            Cambios sin aplicar: presioná Recalcular
                        </Tag>}
                </Typography.Paragraph>
            </Card>}

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: "1rem" }}
                message={`Se cuentan las infracciones renombradas dentro del período seleccionado (fecha de renombrado), sin importar cuándo se asignó el lote.`}
            />

            <Space style={{ marginBottom: "0.5rem" }}>
                <Switch checked={ocultarSinActividad} onChange={setOcultarSinActividad} size="small" />
                <span>Ocultar renombradores sin actividad</span>
            </Space>

            <Table<FilaResumen & { Importe: number }>
                bordered
                size="middle"
                loading={loading}
                dataSource={filasVisibles}
                pagination={false}
                title={() => <b>{`Resumen de renombrado${consulta ? ` - ${consulta.Periodo}` : ""}`}{progreso ? ` (${progreso.actual}/${progreso.total})` : ""}</b>}
                locale={{ emptyText: filas ? "Sin datos para el período" : "Seleccioná un período y presioná Filtrar" }}
                summary={() => filasVisibles.length
                    ? <Table.Summary.Row>
                        <Table.Summary.Cell index={0}><b>Total</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={1}><b>{totales.Renombradas}</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={2}><b>{totales.Efectivas}</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={3}><b>{totales.Ilegibles}</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                            <b>{totales.Renombradas ? `${(totales.Efectivas * 100 / totales.Renombradas).toFixed(2)}%` : "-"}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}><b>{formatoMoneda(totales.Importe)}</b></Table.Summary.Cell>
                    </Table.Summary.Row>
                    : undefined}>

                <Table.Column<FilaResumen>
                    title="Renombrador"
                    dataIndex="Renombrador"
                    sorter={(a, b) => a.Renombrador.localeCompare(b.Renombrador)}
                />
                <Table.Column<FilaResumen>
                    title="Renombradas"
                    dataIndex="Renombradas"
                    sorter={(a, b) => a.Renombradas - b.Renombradas}
                />
                <Table.Column<FilaResumen>
                    title="Efectivas"
                    dataIndex="Efectivas"
                    defaultSortOrder="descend"
                    sorter={(a, b) => a.Efectivas - b.Efectivas}
                    render={(value: number) => <b>{value}</b>}
                />
                <Table.Column<FilaResumen>
                    title="Ilegibles"
                    dataIndex="Ilegibles"
                    sorter={(a, b) => a.Ilegibles - b.Ilegibles}
                />
                <Table.Column<FilaResumen>
                    title="Efectividad"
                    sorter={(a, b) => (a.Renombradas ? a.Efectivas / a.Renombradas : 0) - (b.Renombradas ? b.Efectivas / b.Renombradas : 0)}
                    render={(_, record) => record.Renombradas ? `${(record.Efectivas * 100 / record.Renombradas).toFixed(2)}%` : "-"}
                />
                <Table.Column<FilaResumen & { Importe: number }>
                    title={puedeVerFormula
                        ? <Tooltip title={`Efectivas × (${formula.ImporteInicial} + REDONDEAR(LOG10(Efectivas); 2) × ${formula.FactorCorreccion})`}>Importe</Tooltip>
                        : "Importe"}
                    dataIndex="Importe"
                    sorter={(a, b) => a.Importe - b.Importe}
                    render={(value: number) => formatoMoneda(value)}
                />
            </Table>
        </>
    );
};
